import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Loader2, Search, X } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { MultiSelect } from 'components/ui/multi-select';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { useCountries, useSpecialties, useSubdivisions } from 'hooks/useReferentials';
import { useDebouncedSearch } from 'hooks/useDebouncedSearch';
import { allSubdivisionsLabel, subdivisionLabel } from 'utils/subdivisionLabel';
import { availabilityLabel } from 'utils/unavailability';
import { AVAILABILITY_STATES, type AvailabilityState } from 'api/unavailabilitiesApi';
import { hasActiveFilters, type TechnicianFilters } from 'api/techniciansApi';

const ANY_STATUS = '__any__';
/** Same sentinel trick as the status select: Radix has no value for "no choice". */
const ANY_AVAILABILITY = '__anyAvailability__';
/** The design's menu item: 34 px, 13 px, weight 500, picked = 600 + check. Mirrors MultiSelect's summary items. */
const STATUS_ITEM = 'h-[34px] rounded-[7px] pl-[11px] pr-9 text-[13px] font-medium data-[state=checked]:font-semibold';

/** An empty multi-select clears the filter entirely rather than sending an empty list. */
const orUndefined = <T,>(values: T[]): T[] | undefined => (values.length ? values : undefined);

interface TechniciansFiltersBarProps {
  filters: TechnicianFilters;
  onChange: (partial: Partial<TechnicianFilters>) => void;
  onReset: () => void;
  onExport: () => void;
  exporting: boolean;
  canExport: boolean;
}

/**
 * One row of labelled triggers ("Pays  Tous ▾"), as the design draws it: the label sits inside
 * the button, so nothing stacks above the fields and the table starts 100 px higher.
 * The six filters the spec lists, availability included since L3 measures it.
 * Pickers offer active values only — displays never filter on `is_active`, only these do.
 */
const TechniciansFiltersBar = ({
  filters,
  onChange,
  onReset,
  onExport,
  exporting,
  canExport,
}: TechniciansFiltersBarProps) => {
  const { t } = useTranslation();
  const { data: countries, isLoading: loadingCountries } = useCountries();
  const { data: subdivisions, isLoading: loadingAreas } = useSubdivisions();
  const { data: specialties, isLoading: loadingSpecialties } = useSpecialties();

  const [searchInput, setSearchInput] = useDebouncedSearch(filters.search, (search) =>
    onChange({ search })
  );

  const countryOptions = (countries ?? [])
    .filter((country) => country.is_active)
    .map((country) => ({ value: country.code, label: country.name }));

  // Areas belong to a country: without the cascade one could ask for Spain and Rhône together
  const areaOptions = useMemo(
    () =>
      (subdivisions ?? [])
        .filter((area) => area.is_active)
        .filter((area) => !filters.countryCode?.length || filters.countryCode.includes(area.country_code))
        .map((area) => ({ value: String(area.id), label: `${area.code} — ${area.name}` })),
    [subdivisions, filters.countryCode]
  );

  const specialtyOptions = (specialties ?? [])
    .filter((specialty) => specialty.is_active)
    .map((specialty) => ({ value: String(specialty.id), label: specialty.name }));

  /** Expertises hang under specialties, so the picker narrows the same way. Deduplicated. */
  const expertiseOptions = useMemo(() => {
    const wanted = filters.specialtyId;
    const byId = new Map(
      (specialties ?? [])
        .filter((specialty) => !wanted?.length || wanted.includes(specialty.id))
        .flatMap((specialty) => specialty.expertises)
        .filter((expertise) => expertise.is_active)
        .map((expertise) => [expertise.id, expertise])
    );
    return Array.from(byId.values()).map((expertise) => ({
      value: String(expertise.id),
      label: expertise.name,
    }));
  }, [specialties, filters.specialtyId]);

  // The subdivision wording belongs to a country, so it only reads true when exactly one is picked
  const country = filters.countryCode?.length === 1
    ? (countries ?? []).find((entry) => entry.code === filters.countryCode?.[0])
    : undefined;
  const areaLabel = country ? subdivisionLabel(t, country.subdivision_type, 2) : t('Areas covered');
  // "Tous" or "Toutes" follows the label's gender, so the empty value is gendered the same way
  const anyArea = country ? allSubdivisionsLabel(t, country.subdivision_type) : t('Any area');

  /** Changing countries drops the out-of-scope areas in the same update, never a render later. */
  const handleCountries = (values: string[]) => {
    const countryCodes = orUndefined(values);
    const reachable = new Set(
      (subdivisions ?? [])
        .filter((area) => !countryCodes || countryCodes.includes(area.country_code))
        .map((area) => area.id)
    );
    onChange({
      countryCode: countryCodes,
      geoAreaId: orUndefined((filters.geoAreaId ?? []).filter((id) => reachable.has(id))),
    });
  };

  /** Same shape one level down: narrowing the specialties drops the expertises they carried. */
  const handleSpecialties = (values: string[]) => {
    const specialtyIds = orUndefined(values.map(Number));
    const reachable = new Set(
      (specialties ?? [])
        .filter((specialty) => !specialtyIds || specialtyIds.includes(specialty.id))
        .flatMap((specialty) => specialty.expertises)
        .map((expertise) => expertise.id)
    );
    onChange({
      specialtyId: specialtyIds,
      expertiseId: orUndefined((filters.expertiseId ?? []).filter((id) => reachable.has(id))),
    });
  };

  // No panel around the row: the next lot makes it the table card's header.
  return (
    <div className="flex flex-wrap items-center gap-2.5">
      <div className="relative min-w-[240px] flex-1">
        <Search
          className="absolute left-[11px] top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/70"
          aria-hidden="true"
        />
        <Input
          aria-label={t('Search')}
          placeholder={t('Search a technician, a company, a country, an area…')}
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          className="h-[38px] rounded-[9px] bg-background pl-[35px] shadow-none md:text-[13.5px]"
        />
      </div>

      <MultiSelect
        summary
        label={t('Country')}
        options={countryOptions}
        selected={filters.countryCode ?? []}
        onChange={handleCountries}
        placeholder={
          loadingCountries
            ? t('Loading…')
            : countryOptions.length === 0
              ? t('No country in the referential yet')
              : t('Any country')
        }
        disabled={loadingCountries || countryOptions.length === 0}
      />

      <MultiSelect
        summary
        label={areaLabel}
        options={areaOptions}
        selected={(filters.geoAreaId ?? []).map(String)}
        onChange={(values) => onChange({ geoAreaId: orUndefined(values.map(Number)) })}
        placeholder={
          loadingAreas
            ? t('Loading…')
            : areaOptions.length === 0
              ? t('No area for this country yet')
              : anyArea
        }
        disabled={loadingAreas || areaOptions.length === 0}
        searchable
      />

      <MultiSelect
        summary
        label={t('Specialty')}
        options={specialtyOptions}
        selected={(filters.specialtyId ?? []).map(String)}
        onChange={handleSpecialties}
        placeholder={
          loadingSpecialties
            ? t('Loading…')
            : specialtyOptions.length === 0
              ? t('No active specialty yet')
              : t('Any specialty')
        }
        disabled={loadingSpecialties || specialtyOptions.length === 0}
        searchable
      />

      <MultiSelect
        summary
        label={t('Expertise')}
        options={expertiseOptions}
        selected={(filters.expertiseId ?? []).map(String)}
        onChange={(values) => onChange({ expertiseId: orUndefined(values.map(Number)) })}
        placeholder={
          loadingSpecialties
            ? t('Loading…')
            : expertiseOptions.length === 0
              ? t('No active expertise yet')
              : t('Any expertise')
        }
        disabled={loadingSpecialties || expertiseOptions.length === 0}
        searchable
      />

      {/* Availability and status are two questions, so two filters: a technician who is away
          keeps an active account, and merging them would make one of the two unaskable. */}
      <Select
        value={filters.availability ?? ANY_AVAILABILITY}
        onValueChange={(value) =>
          onChange({
            availability: value === ANY_AVAILABILITY ? undefined : (value as AvailabilityState),
          })
        }
      >
        <SelectTrigger
          aria-label={t('Availability')}
          className="h-[38px] w-auto gap-[7px] rounded-[9px] bg-background text-[13px] shadow-none hover:border-zinc-300 focus:ring-0 focus-visible:ring-1 focus-visible:ring-ring data-[state=open]:border-zinc-900 data-[state=open]:ring data-[state=open]:ring-zinc-100 dark:hover:border-zinc-600 dark:data-[state=open]:border-zinc-300 dark:data-[state=open]:ring-zinc-800"
        >
          <span className="text-muted-foreground">{t('Availability')}</span>
          <SelectValue className="font-medium" />
        </SelectTrigger>
        <SelectContent className="min-w-[212px] rounded-[10px] [&_[data-radix-select-viewport]]:p-[5px]">
          <SelectItem className={STATUS_ITEM} value={ANY_AVAILABILITY}>
            {t('Any availability')}
          </SelectItem>
          {AVAILABILITY_STATES.map((state) => (
            <SelectItem key={state} className={STATUS_ITEM} value={state}>
              {availabilityLabel(t, state)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={filters.status ?? ANY_STATUS}
        onValueChange={(value) =>
          onChange({ status: value === ANY_STATUS ? undefined : (value as 'active' | 'inactive') })
        }
      >
        {/* Same one-button look as the multi-selects: label muted, value medium, chevron from the trigger */}
        <SelectTrigger
          aria-label={t('Status')}
          // Same hover and zinc open state as its four MultiSelect neighbours, so the row reads as one.
          className="h-[38px] w-auto gap-[7px] rounded-[9px] bg-background text-[13px] shadow-none hover:border-zinc-300 focus:ring-0 focus-visible:ring-1 focus-visible:ring-ring data-[state=open]:border-zinc-900 data-[state=open]:ring data-[state=open]:ring-zinc-100 dark:hover:border-zinc-600 dark:data-[state=open]:border-zinc-300 dark:data-[state=open]:ring-zinc-800"
        >
          <span className="text-muted-foreground">{t('Status')}</span>
          <SelectValue className="font-medium" />
        </SelectTrigger>
        <SelectContent className="min-w-[212px] rounded-[10px] [&_[data-radix-select-viewport]]:p-[5px]">
          <SelectItem className={STATUS_ITEM} value={ANY_STATUS}>{t('Any status')}</SelectItem>
          <SelectItem className={STATUS_ITEM} value="active">{t('Active')}</SelectItem>
          <SelectItem className={STATUS_ITEM} value="inactive">{t('Inactive')}</SelectItem>
        </SelectContent>
      </Select>

      {/* Conditional, like the mock-up: nothing to reset, nothing to offer. */}
      {hasActiveFilters(filters) && (
        <Button
          variant="outline"
          onClick={onReset}
          className="h-[38px] gap-1.5 rounded-[9px] px-3 text-[13px] text-muted-foreground hover:text-foreground [&_svg]:size-3.5"
        >
          <X />
          {t('Reset')}
        </Button>
      )}

      <Button
        variant="outline"
        onClick={onExport}
        disabled={exporting || !canExport}
        className="h-[38px] gap-[7px] rounded-[9px] border-zinc-300 px-[13px] text-[13px] dark:border-zinc-600"
      >
        {exporting ? (
          <Loader2 className="animate-spin" aria-hidden="true" />
        ) : (
          <Download />
        )}
        {t('Export CSV')}
      </Button>
    </div>
  );
};

export default TechniciansFiltersBar;
